import type { VehicleInsurance, VehicleInsuranceSaveRequestV2 } from "@credit-acceptance/step6-bff-client";
import { z } from "zod";

import { isRealIsoDate } from "./ContractVehicleInformation.schema";

// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const INSURER_NAME_MESSAGE = "Please enter insurance company name.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const INSURER_PHONE_MESSAGE = "Please enter the area code and 7 digit insurer Phone Number.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const EXPIRATION_DATE_INVALID_MESSAGE = "Expiration Date is invalid (MM/DD/YYYY).";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const COMP_REQUIRED_MESSAGE = "Please enter Comprehensive Deductible.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const COMP_MAX_MESSAGE = "Comprehensive Deductible should be lesser or equal to $1,000.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const COLLISION_REQUIRED_MESSAGE = "Please enter Collision Deductible.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const COLLISION_MAX_MESSAGE = "Collision Deductible should be lesser or equal to $1,000.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const POLICY_NUMBER_LENGTH_MESSAGE = "Must be 100 characters or fewer";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const AGENT_NAME_LENGTH_MESSAGE = "Must be 40 characters or fewer";

/** A fully-zeroed phone number is the legacy placeholder — treated the same as blank. */
const BLANK_PHONE_PLACEHOLDER = "000-000-0000";

// ---------------------------------------------------------------------------
// insuranceInformationSchema — RHF + Zod for InsuranceInformation.tsx, same
// dual-mode TextField/CurrencyField/DateField/PhoneField/LossPayeeField/
// FullCoverageField pattern as ContractVehicleInformation.schema.ts. Amount
// Financed stays LabeledValue-driven, outside this schema (it's sourced from
// `financial.amountFinanced`, not `VehicleInsurance`).
// ---------------------------------------------------------------------------
export const FULL_COVERAGE_VALUES = ["Y", "N"] as const;
export type FullCoverageValue = (typeof FULL_COVERAGE_VALUES)[number];

export const POLICY_NUMBER_MAX_LENGTH = 100;
export const AGENT_NAME_MAX_LENGTH = 40;
export const DEDUCTIBLE_MAX = 1000;
export const DEDUCTIBLE_MAX_LENGTH = 7;
export const INSURER_NAME_MAX_LENGTH = 100;
const PHONE_DIGITS = 10;

export const insuranceInformationSchema = z
  .object({
    // Mapped from the boolean VehicleInsurance.fullCoverage; undefined hides the field. Never validated itself.
    fullCoverage: z.enum(FULL_COVERAGE_VALUES).optional(),
    policyNumber: z.string().max(POLICY_NUMBER_MAX_LENGTH, POLICY_NUMBER_LENGTH_MESSAGE),
    insurerName: z.string().trim().min(1, INSURER_NAME_MESSAGE),
    // Required check (blank or the placeholder mask) lives in the superRefine below —
    // the placeholder-as-blank rule isn't expressible as a plain string check here.
    insurerPhone: z.string(),
    agentName: z.string().max(AGENT_NAME_MAX_LENGTH, AGENT_NAME_LENGTH_MESSAGE),
    // No hard requirement enforced — matches the legacy "cosmetic-only, doesn't block save" behavior.
    expirationDate: z.string(),
    // Nullable (not just optional) — react-hook-form's `useController` cannot
    // reactively clear a field to `undefined` once it's had a real default (a
    // sibling `setValue`/`field.onChange(undefined)` silently falls back to
    // the original default instead), but `null` propagates correctly. `null`
    // is Full Coverage's clear-on-toggle sentinel; both mean "blank" below.
    comprehensiveDeductibleAmount: z.number().nullable().optional(),
    collisionDeductibleAmount: z.number().nullable().optional(),
    // Never validated — just maps DB codes Y/N/U to a display value.
    isCaLossPayee: z.enum(["Y", "N", "U"]).optional(),
  })
  .superRefine((data, context) => {
    // Spec 9.2: these only report (completeness) — the save still proceeds.
    const phoneDigits = data.insurerPhone.replaceAll(/\D/g, "");
    if (phoneDigits.length !== PHONE_DIGITS || data.insurerPhone === BLANK_PHONE_PLACEHOLDER) {
      context.addIssue({ code: "custom", path: ["insurerPhone"], message: INSURER_PHONE_MESSAGE });
    }
    if (data.expirationDate && !isRealIsoDate(data.expirationDate)) {
      context.addIssue({ code: "custom", path: ["expirationDate"], message: EXPIRATION_DATE_INVALID_MESSAGE });
    }

    if (data.fullCoverage !== "Y") {
      return;
    }
    const rules = [
      ["comprehensiveDeductibleAmount", data.comprehensiveDeductibleAmount, COMP_REQUIRED_MESSAGE, COMP_MAX_MESSAGE],
      ["collisionDeductibleAmount", data.collisionDeductibleAmount, COLLISION_REQUIRED_MESSAGE, COLLISION_MAX_MESSAGE],
    ] as const;
    for (const [path, amount, requiredMessage, maxMessage] of rules) {
      // eslint-disable-next-line unicorn/no-null -- `null` is the clear-on-toggle sentinel, see the field comments above
      if (amount == null) {
        context.addIssue({ code: "custom", path: [path], message: requiredMessage });
      } else if (amount > DEDUCTIBLE_MAX) {
        context.addIssue({ code: "custom", path: [path], message: maxMessage });
      }
    }
  });

export type InsuranceInformationInput = z.infer<typeof insuranceInformationSchema>;

/**
 * Builds RHF defaultValues from the real `VehicleInsurance` prop.
 * @param insurance The dashboard response's `VehicleInsurance`, if any.
 * @returns {InsuranceInformationInput} Seed values for `useForm`'s `defaultValues`.
 */
export function buildInsuranceDefaultValues(insurance?: VehicleInsurance): InsuranceInformationInput {
  let fullCoverage: FullCoverageValue | undefined;
  if (insurance?.fullCoverage !== undefined) {
    fullCoverage = insurance.fullCoverage ? "Y" : "N";
  }
  return {
    fullCoverage,
    policyNumber: insurance?.policyNumber ?? "",
    insurerName: insurance?.insurerName ?? "",
    insurerPhone: insurance?.insurerPhone ?? "",
    agentName: insurance?.agentName ?? "",
    expirationDate: insurance?.expirationDate ?? "",
    // eslint-disable-next-line unicorn/no-null -- `null` is the blank sentinel for these fields, see the schema above
    comprehensiveDeductibleAmount: insurance?.comprehensiveDeductibleAmount ?? null,
    // eslint-disable-next-line unicorn/no-null -- see above
    collisionDeductibleAmount: insurance?.collisionDeductibleAmount ?? null,
    isCaLossPayee: insurance?.isCaLossPayee ?? "U",
  };
}

/**
 *
 * @param digits
 * @param amount
 */
function deductibleText(amount?: number | null): string | undefined {
  return typeof amount === "number" ? String(amount) : undefined;
}

/**
 *
 * @param digits
 */
function formatPhone(digits: string): string {
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Maps the insurance form to the save request's `vehicleInsurance` leg. Lic Plate is deliberately absent
 * (Contract Vehicle contributes it; the orchestrator merges the two). An invalid phone or expiration date is
 * left out so the stored value stays untouched — the field itself already shows the error, rather than
 * legacy's silent null. Deductibles are only sent while Full Coverage is Yes.
 * @param values Current form values.
 * @returns {VehicleInsuranceSaveRequestV2} The insurance-leg save payload.
 */
export function toInsuranceSaveRequest(values: InsuranceInformationInput): VehicleInsuranceSaveRequestV2 {
  const phoneDigits = values.insurerPhone.replaceAll(/\D/g, "");
  const hasDeductibles = values.fullCoverage === "Y";
  const request: VehicleInsuranceSaveRequestV2 = {
    insurerName: values.insurerName.trim().toUpperCase() || undefined,
    insurerPhone: phoneDigits.length === PHONE_DIGITS ? formatPhone(phoneDigits) : undefined,
    policy: values.policyNumber || undefined,
    agent: values.agentName.trim().toUpperCase() || undefined,
    effectiveDate: values.expirationDate && isRealIsoDate(values.expirationDate) ? values.expirationDate : undefined,
    fullCoverage: values.fullCoverage === undefined ? undefined : values.fullCoverage === "Y",
    compDeductible: hasDeductibles ? deductibleText(values.comprehensiveDeductibleAmount) : undefined,
    collisionDeductible: hasDeductibles ? deductibleText(values.collisionDeductibleAmount) : undefined,
    selectedCALossPayee: values.isCaLossPayee,
  };
  return request;
}
